'use client'

// The First Short Film Festival, for people: what it is, the programme so far, the rules and the terms, and entering one.
//
// Sam, 4 Nov: "At all posters for the movie night, lets have an action button with a drawer that comes up to show about
// the film festival, the rules and how to submit ... lets make it strict so we don't get flooded". And of the first cut:
// "I just need that UI to look better - right now it feels slapped together". So it is dressed as the festival is: the
// poster's paper, its magenta and its condensed type (Anton, already the site's poster face), the dates as ticket stubs,
// the films as a numbered programme, and entering as three steps. Opened at its posters (FestivalHud) or from its card
// on What's on (CalendarDrawer). The words and limits are lib/festival.js; the gate is app/api/festival (signed in, and
// he has kept something of yours), and the town keeps the entries, each waiting for Sam (sim-server world/Festival.js).
//
// Entering, in the order it happens: pick the film; its length is read and its stills are made here in the browser
// (lib/festivalStills.js) and shown, because the stills are how Pino watches it; a title and a line; the two promises;
// then the file and its stills go straight up to the blob store (app/api/festival/upload hands out the tokens) and the
// entry goes to the town.

import { useCallback, useEffect, useRef, useState } from 'react'
import { upload } from '@vercel/blob/client'
import useGameStore from '../../stores/gameStore'
import { authFetch, useAccount } from '../auth/useUser'
import { Drawer, Card, Pill } from './Drawer'
import { Tabs } from './Tabs'
import { kawaii } from '../../lib/ui/theme'
import {
  POSTER, ABOUT, TERMS, SECONDS, MAX_BYTES, TITLE_MAX, LINE_MAX, VIDEO_TYPES, CLOSES_AT, MOST_FILMS, KNOWN_MIN,
} from '../../lib/festival'
import { stillsOf } from '../../lib/festivalStills'
import { previewAccepted } from '../../lib/ui/rankPreview'

// the poster's own: its paper, its magenta, and its condensed face
const PAPER = '#f4e6cc'
const MAGENTA = '#e0436b'
const DISPLAY = 'var(--font-poster-condensed), Impact, "Arial Narrow", sans-serif'

const P = { color: kawaii.ink, fontSize: 14.5, fontWeight: 600, lineHeight: 1.65, margin: 0 }
const SOFT = { color: kawaii.inkSoft, fontSize: 13, fontWeight: 700, lineHeight: 1.5 }
const KICKER = { fontFamily: DISPLAY, fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase', color: MAGENTA }
const HEAD = { fontFamily: DISPLAY, fontSize: 26, lineHeight: 1.05, letterSpacing: '0.01em', textTransform: 'uppercase', color: kawaii.ink, margin: 0 }

const STATUS = {
  waiting: ['Waiting for a look', '#f0b064'],
  in: ['In the festival', '#8fd19e'],
  refused: ['Not taken', '#f2a3a3'],
  withdrawn: ['Taken back out', kawaii.creamDeep],
}

// the rules as tiles: a heading you can take in at a glance, and the line that says it properly
const RULE_TILES = [
  ['He knows you', 'You can enter if Pino has kept at least one thing you left him at the shrine.'],
  ['One each, twenty in all', `One film per person, ${MOST_FILMS} in the festival. Entries close early if it fills up.`],
  ['Silent, up to a minute', `${SECONDS[0]} to ${SECONDS[1]} seconds. mp4, mov or webm, up to 50 MB. Its sound is never played.`],
  ['Your own work, any tools', 'A camera, drawings, animation or AI tools (Pino makes his with Runway), as long as it\'s yours and you can use everything in it.'],
  ['Nothing that hurts', 'No real people who haven\'t agreed to be in it. Nothing sexual, hateful, cruel or against the law. No one else\'s film, video or advert.'],
  ['A person checks each', 'Every film is watched before it goes in, and any film can be refused. Pino hears about a film once it\'s in.'],
]

const daysLeft = (now = Date.now()) => Math.ceil((CLOSES_AT - now) / 86_400_000)
function countdown(now = Date.now()) {
  if (now >= CLOSES_AT) return 'Entries have closed'
  const d = daysLeft(now)
  return d <= 1 ? 'Last day to enter' : `${d} days left to enter`
}

export function FestivalDrawer() {
  const open = useGameStore((s) => s.festivalOpen)
  const setOpen = useGameStore((s) => s.setFestivalOpen)
  const [data, setData] = useState(null)
  const [tab, setTab] = useState('festival')
  // (a tab opens at its top, not wherever the last one was scrolled to: the drawer's body is the one scroller)
  const body = useRef(null)
  useEffect(() => { body.current?.closest('.kawaii-scroll')?.scrollTo({ top: 0 }) }, [tab])

  const load = useCallback(() => {
    authFetch('/api/festival', { cache: 'no-store' })
      .then((r) => r.json())
      .then((b) => setData(b || {}))
      .catch(() => setData({}))
  }, [])
  useEffect(() => { if (open) load() }, [open, load])

  if (!open) return null
  const TABS = [
    { id: 'festival', label: 'The festival' },
    { id: 'rules', label: 'Rules' },
    { id: 'enter', label: 'Enter a film' },
  ]
  return (
    <Drawer header={<Tabs tabs={TABS} active={tab} onPick={setTab} />} onClose={() => { setTab('festival'); setOpen(false) }}>
      <div ref={body} className="max-w-5xl mx-auto">
        {tab === 'festival' && <Festival data={data} toEnter={() => setTab('enter')} />}
        {tab === 'rules' && <Rules toEnter={() => setTab('enter')} />}
        {tab === 'enter' && <Entry data={data} reload={load} toRules={() => setTab('rules')} />}
      </div>
    </Drawer>
  )
}

// ── The festival ────────────────────────────────────────────────────────

function Festival({ data, toEnter }) {
  const places = data?.places ?? MOST_FILMS
  return (
    <div className="flex flex-col" style={{ gap: 22 }}>
      <Hero data={data} places={places} toEnter={toEnter} />
      <div className="grid gap-5 md:grid-cols-5 items-start">
        <Card style={{ padding: '18px 20px' }} className="md:col-span-2">
          <div style={KICKER}>How Pino watches</div>
          <FrameGrid />
          <p style={{ ...P, fontSize: 13.5 }}>{ABOUT[2]}</p>
        </Card>
        <div className="md:col-span-3 min-w-0">
          <Programme data={data} />
        </div>
      </div>
    </div>
  )
}

function Hero({ data, places, toEnter }) {
  return (
    <div
      style={{
        position: 'relative', background: PAPER, border: kawaii.outline, borderRadius: 22, boxShadow: kawaii.drop,
        padding: 'clamp(16px, 3vw, 26px)', overflow: 'hidden',
      }}
    >
      {/* a halftone wash in the corner, as the poster is printed */}
      <div aria-hidden style={{ position: 'absolute', right: -40, top: -40, width: 260, height: 260, borderRadius: '50%', background: `radial-gradient(${MAGENTA}33 1.2px, transparent 1.6px) 0 0 / 9px 9px`, pointerEvents: 'none' }} />
      <div className="flex flex-col sm:flex-row" style={{ gap: 'clamp(16px, 3vw, 28px)', position: 'relative' }}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={POSTER}
          alt="The First Short Film Festival poster"
          className="self-center sm:self-start"
          style={{ width: 'clamp(130px, 22vw, 190px)', aspectRatio: '2 / 3', objectFit: 'cover', border: kawaii.outline, borderRadius: 8, boxShadow: `6px 7px 0 ${kawaii.ink}`, transform: 'rotate(-2.2deg)', flex: 'none', background: '#fff' }}
        />
        <div className="min-w-0 flex flex-col" style={{ gap: 12 }}>
          <div style={KICKER}>Call for entries · presented with Runway</div>
          <h2 style={{ fontFamily: DISPLAY, fontSize: 'clamp(34px, 5.4vw, 58px)', lineHeight: 0.92, textTransform: 'uppercase', color: MAGENTA, margin: 0, textShadow: `2px 2px 0 ${kawaii.ink}` }}>
            The First Short<br />Film Festival
          </h2>
          <p style={{ ...P, maxWidth: 560 }}>{ABOUT[0]}</p>
          <p style={{ ...P, maxWidth: 560, color: kawaii.inkSoft }}>{ABOUT[1]}</p>
          <div className="grid gap-2.5 sm:grid-cols-3" style={{ marginTop: 4 }}>
            <Stub label="Entries close" big="Thu 12 Nov" small="midnight, UK time" />
            <Stub label="Festival night" big="Sat 14 Nov · 8pm" small="at the cinema" hot />
            <Stub label="Opening film" big="Lumière, 1896" small="a train arriving at La Ciotat" />
          </div>
          <div className="flex items-center flex-wrap" style={{ gap: 10, marginTop: 4 }}>
            <button type="button" onClick={toEnter} style={bigButton(true)}>Enter a film →</button>
            <Pill bg={kawaii.ink}>{countdown()}</Pill>
            {data && <Pill bg={places > 0 ? '#8fd19e' : '#f2a3a3'}>{places > 0 ? `${places} of ${MOST_FILMS} places left` : 'Full'}</Pill>}
          </div>
        </div>
      </div>
    </div>
  )
}

// a date as a ticket stub: a perforated edge, what it is, and when
function Stub({ label, big, small, hot = false }) {
  return (
    <div
      style={{
        position: 'relative', background: hot ? MAGENTA : kawaii.cream, color: hot ? '#fff' : kawaii.ink,
        border: kawaii.outlineThin, borderRadius: 12, padding: '9px 12px 9px 18px', boxShadow: kawaii.dropSm,
      }}
    >
      <span aria-hidden style={{ position: 'absolute', left: 8, top: 8, bottom: 8, borderLeft: `2px dashed ${hot ? 'rgba(255,255,255,0.6)' : 'rgba(47,38,80,0.3)'}` }} />
      <div style={{ fontSize: 10.5, fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', opacity: 0.8 }}>{label}</div>
      <div style={{ fontFamily: DISPLAY, fontSize: 21, lineHeight: 1.1, textTransform: 'uppercase', margin: '2px 0 1px' }}>{big}</div>
      <div style={{ fontSize: 12, fontWeight: 700, opacity: 0.8 }}>{small}</div>
    </div>
  )
}

// twelve frames, four across, in the order they play: what a film is to him
function FrameGrid() {
  return (
    <div aria-hidden style={{ background: kawaii.ink, borderRadius: 10, padding: 8, margin: '10px 0 12px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
      {Array.from({ length: 12 }, (_, i) => (
        <div key={i} style={{ aspectRatio: '16 / 9', borderRadius: 3, background: `linear-gradient(135deg, ${i % 3 === 0 ? MAGENTA : '#5b4f8f'}55, #ffffff22)`, display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', padding: 2 }}>
          <span style={{ fontSize: 8, fontWeight: 900, color: '#ffffffaa' }}>{i + 1}</span>
        </div>
      ))}
    </div>
  )
}

function Programme({ data }) {
  const [playing, setPlaying] = useState(null)
  const films = data?.films || []
  return (
    <div>
      <div className="flex items-baseline justify-between" style={{ gap: 12, marginBottom: 10 }}>
        <h3 style={HEAD}>The programme so far</h3>
        <span style={SOFT}>{films.length ? `${films.length} ${films.length === 1 ? 'film' : 'films'}` : ''}</span>
      </div>
      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))' }}>
        <ProgrammeOpening />
        {films.map((f, i) => (
          <div key={`${f.title}-${i}`} style={{ minWidth: 0 }}>
            <div style={{ position: 'relative' }}>
              {f.video && playing === i ? (
                // silent: the festival never plays a film's sound
                <video src={f.video} muted autoPlay loop playsInline onClick={() => setPlaying(null)} style={still(true)} />
              ) : f.poster ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={f.poster} alt="" onClick={() => f.video && setPlaying(i)} style={{ ...still(!!f.video), objectFit: 'cover' }} />
              ) : (
                <div style={{ ...still(false), background: f.his ? MAGENTA : kawaii.night }} />
              )}
              <FilmNo n={i + 1} />
              {f.video && playing !== i && <span aria-hidden style={{ position: 'absolute', right: 8, bottom: 8, width: 26, height: 26, borderRadius: 999, background: kawaii.cream, border: kawaii.outlineThin, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: kawaii.ink }}>▶</span>}
            </div>
            <div style={{ fontFamily: DISPLAY, fontSize: 17, lineHeight: 1.15, textTransform: 'uppercase', color: kawaii.ink, marginTop: 6 }}>{f.title}</div>
            <div style={SOFT}>{f.his ? 'by Pino' : `by ${f.by}${f.seconds ? ` · ${Math.round(f.seconds)}s` : ''}`}</div>
          </div>
        ))}
        {films.length < 3 && Array.from({ length: 3 - films.length }, (_, i) => (
          <div key={`open-${i}`} style={{ ...still(false), background: 'transparent', border: `2px dashed rgba(47,38,80,0.3)`, boxShadow: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ ...SOFT, fontSize: 12.5 }}>{i === 0 ? 'Your film here' : 'A place to fill'}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function ProgrammeOpening() {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ ...still(false), background: kawaii.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <span style={{ fontFamily: DISPLAY, color: PAPER, fontSize: 15, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Opening film</span>
      </div>
      <div style={{ fontFamily: DISPLAY, fontSize: 17, lineHeight: 1.15, textTransform: 'uppercase', color: kawaii.ink, marginTop: 6 }}>L&rsquo;Arrivée d&rsquo;un train</div>
      <div style={SOFT}>the Lumière brothers · 1896</div>
    </div>
  )
}

const still = (clickable) => ({ width: '100%', aspectRatio: '16 / 9', display: 'block', background: '#000', borderRadius: 10, border: kawaii.outlineThin, boxShadow: kawaii.dropSm, cursor: clickable ? 'pointer' : 'default' })
function FilmNo({ n }) {
  return <span style={{ position: 'absolute', left: 8, top: 8, fontFamily: DISPLAY, fontSize: 14, background: PAPER, color: kawaii.ink, border: kawaii.outlineThin, borderRadius: 7, padding: '0 6px', lineHeight: 1.5 }}>{String(n).padStart(2, '0')}</span>
}

// ── Rules ───────────────────────────────────────────────────────────────

function Rules({ toEnter }) {
  return (
    <div className="flex flex-col" style={{ gap: 20 }}>
      <div>
        <div style={KICKER}>Strict, so it stays good</div>
        <h3 style={{ ...HEAD, fontSize: 32 }}>The rules</h3>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {RULE_TILES.map(([h, line], i) => (
          <Card key={h} style={{ padding: '14px 16px', display: 'flex', gap: 12 }}>
            <span style={{ fontFamily: DISPLAY, fontSize: 30, lineHeight: 1, color: MAGENTA, flex: 'none', width: 34 }}>{String(i + 1).padStart(2, '0')}</span>
            <div className="min-w-0">
              <div style={{ fontSize: 15, fontWeight: 900, color: kawaii.ink, marginBottom: 3 }}>{h}</div>
              <div style={{ ...P, fontSize: 13.5, lineHeight: 1.5 }}>{line}</div>
            </div>
          </Card>
        ))}
      </div>
      <div className="grid gap-3 md:grid-cols-3 items-start">
        <Card style={{ background: PAPER }} className="md:col-span-1">
          <div style={{ fontSize: 15, fontWeight: 900, marginBottom: 6 }}>When</div>
          <p style={{ ...P, fontSize: 13.5 }}>Entries close at midnight (UK) at the end of Thursday 12 November. You can take your film back out yourself any time before festival night.</p>
        </Card>
        <Card className="md:col-span-2">
          <div style={{ fontSize: 15, fontWeight: 900, marginBottom: 6 }}>The small print</div>
          <ul style={{ margin: 0, paddingLeft: 18, listStyle: 'disc' }}>
            {TERMS.map((t) => <li key={t} style={{ ...P, fontSize: 13, lineHeight: 1.55, marginBottom: 5 }}>{t}</li>)}
          </ul>
        </Card>
      </div>
      <div><button type="button" onClick={toEnter} style={bigButton(true)}>Enter a film →</button></div>
    </div>
  )
}

// ── Entering ────────────────────────────────────────────────────────────

function bigButton(on) {
  return {
    background: on ? MAGENTA : kawaii.creamDeep, color: on ? '#fff' : kawaii.inkSoft, border: kawaii.outline,
    borderRadius: 999, padding: '10px 22px', fontFamily: DISPLAY, fontSize: 17, letterSpacing: '0.06em', textTransform: 'uppercase',
    cursor: on ? 'pointer' : 'default', boxShadow: on ? kawaii.drop : 'none', transition: 'transform 120ms ease',
  }
}
const quietButton = { background: kawaii.cream, color: kawaii.ink, border: kawaii.outlineThin, borderRadius: 999, padding: '7px 16px', fontWeight: 800, fontSize: 13.5, cursor: 'pointer', boxShadow: kawaii.dropSm }

function StateCard({ title, children, action = null }) {
  return (
    <Card style={{ padding: '26px 24px', textAlign: 'center', background: PAPER, maxWidth: 560, margin: '8px auto' }}>
      <div style={{ ...HEAD, fontSize: 28, marginBottom: 8 }}>{title}</div>
      <p style={{ ...P, maxWidth: 440, margin: '0 auto' }}>{children}</p>
      {action && <div style={{ marginTop: 16 }}>{action}</div>}
    </Card>
  )
}

function Entry({ data, reload, toRules }) {
  const { signedIn } = useAccount()
  const setProfileOpen = useGameStore((s) => s.setProfileOpen)
  const [busy, setBusy] = useState(false)
  const [said, setSaid] = useState(null)
  // (dev only, as What's on's ladder is: ?rank=new|knows stands you on that rung, so the form can be looked at)
  const [forced] = useState(previewAccepted)

  if (!data) return <p style={SOFT}>reading…</p>
  if (!signedIn && forced == null) {
    return (
      <StateCard title="Sign in to enter" action={<button type="button" style={bigButton(true)} onClick={() => setProfileOpen(true)}>Sign in</button>}>
        Entering is for people Pino knows, so it starts with knowing who you are.
      </StateCard>
    )
  }
  const you = forced != null ? { ...(data.you || {}), known: forced >= KNOWN_MIN } : (data.you || {})
  const mine = you.entry
  const standing = mine && (mine.status === 'waiting' || mine.status === 'in')

  const withdraw = async () => {
    if (!window.confirm(`Take "${mine.title}" back out of the festival?`)) return
    setBusy(true)
    try {
      const r = await authFetch('/api/festival', { method: 'DELETE' })
      const b = await r.json().catch(() => ({}))
      setSaid(r.ok ? 'Taken back out.' : b.error || 'That did not work.')
      reload()
    } finally { setBusy(false) }
  }

  return (
    <div className="flex flex-col" style={{ gap: 16 }}>
      {mine && (
        <Card style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', background: PAPER }}>
          {mine.poster && (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={mine.poster} alt="" style={{ width: 120, aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 8, border: kawaii.outlineThin, flex: 'none' }} />
          )}
          <div className="min-w-0 flex-1">
            <div style={{ ...SOFT, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Your film</div>
            <div style={{ fontFamily: DISPLAY, fontSize: 22, textTransform: 'uppercase', lineHeight: 1.1 }}>{mine.title}</div>
            <div style={{ marginTop: 6 }}><Pill bg={(STATUS[mine.status] || STATUS.waiting)[1]}>{(STATUS[mine.status] || [mine.status])[0]}</Pill></div>
            {mine.status === 'waiting' && <p style={{ ...SOFT, marginTop: 8 }}>A person watches every film before it goes in. Pino hears about yours once it does.</p>}
            {mine.status === 'in' && <p style={{ ...SOFT, marginTop: 8 }}>It&rsquo;s in. Pino watches it on festival night.</p>}
            {mine.status === 'refused' && <p style={{ ...SOFT, marginTop: 8 }}>{mine.why ? `Why: ${mine.why}. ` : ''}You can enter another.</p>}
          </div>
          {standing && <button type="button" style={quietButton} disabled={busy} onClick={withdraw}>Take it back out</button>}
        </Card>
      )}
      {said && <p style={{ ...SOFT, color: kawaii.ink, textAlign: 'center' }}>{said}</p>}
      {!standing && (
        !you.known ? (
          <StateCard title="Pino has to know you first">
            Leave him something at the shrine. Once he keeps something of yours, you can enter a film.
          </StateCard>
        ) : !data.open ? (
          <StateCard title={Date.now() >= CLOSES_AT ? 'Entries have closed' : 'The festival is full'}>
            {Date.now() >= CLOSES_AT ? 'See you on festival night, Saturday 14 November at 8pm.' : `All ${MOST_FILMS} places are taken. If one comes free, you can enter.`}
          </StateCard>
        ) : (
          <EntryForm onDone={(msg) => { setSaid(null); reload(); setSaid(msg) }} toRules={toRules} />
        )
      )}
    </div>
  )
}

function Step({ n, title, done, children }) {
  return (
    <div className="flex" style={{ gap: 14 }}>
      <div className="flex flex-col items-center" style={{ flex: 'none' }}>
        <span style={{ width: 34, height: 34, borderRadius: 999, border: kawaii.outlineThin, background: done ? '#8fd19e' : PAPER, color: kawaii.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: DISPLAY, fontSize: 17, boxShadow: kawaii.dropSm }}>{done ? '✓' : n}</span>
        <span aria-hidden style={{ flex: 1, width: 2, background: 'rgba(47,38,80,0.18)', marginTop: 6 }} />
      </div>
      <div className="min-w-0 flex-1" style={{ paddingBottom: 18 }}>
        <div style={{ fontFamily: DISPLAY, fontSize: 20, textTransform: 'uppercase', lineHeight: 1.2, marginBottom: 8, color: kawaii.ink }}>{title}</div>
        {children}
      </div>
    </div>
  )
}

function EntryForm({ onDone, toRules }) {
  const [file, setFile] = useState(null)
  const [stills, setStills] = useState(null)
  const [preview, setPreview] = useState(null)
  const [title, setTitle] = useState('')
  const [line, setLine] = useState('')
  const [made, setMade] = useState(false)
  const [agree, setAgree] = useState(false)
  const [step, setStep] = useState(null)
  const [progress, setProgress] = useState(null)
  const [error, setError] = useState(null)
  const [over, setOver] = useState(false)
  const input = useRef(null)

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview) }, [preview])

  const pick = async (f) => {
    setError(null); setStills(null); setFile(null)
    if (preview) { URL.revokeObjectURL(preview); setPreview(null) }
    if (!f) return
    const okType = VIDEO_TYPES.includes(f.type) || /\.(mp4|mov|webm)$/i.test(f.name)
    if (!okType) return setError('It has to be an mp4, mov or webm.')
    if (f.size > MAX_BYTES) return setError('It has to be 50 MB or less.')
    setStep('Reading it and making its stills…')
    try {
      const s = await stillsOf(f)
      if (s.seconds < SECONDS[0] || s.seconds > SECONDS[1] + 0.5) throw new Error(`It runs ${Math.round(s.seconds)} seconds. A film is ${SECONDS[0]} to ${SECONDS[1]}.`)
      setFile(f); setStills(s); setPreview(URL.createObjectURL(s.sheet))
    } catch (err) {
      setError(err.message)
    } finally { setStep(null) }
  }

  const ready = file && stills && title.trim() && made && agree && !step
  const send = async () => {
    if (!ready) return
    setError(null)
    try {
      const token = await useGameStore.getState().authApi?.getToken?.().catch(() => null)
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      const stamp = Date.now()
      const ext = (file.name.match(/\.(mp4|mov|webm)$/i)?.[1] || 'mp4').toLowerCase()
      // the film is most of the weight: it fills the bar to 90, its stills and poster the rest
      const put = (name, body, contentType, label, from, span) => upload(`festival/${stamp}-${name}`, body, {
        access: 'public', handleUploadUrl: '/api/festival/upload', headers, contentType,
        onUploadProgress: (p) => { setStep(label); setProgress(from + (span * p.percentage) / 100) },
      })
      const video = await put(`film.${ext}`, file, file.type || 'video/mp4', 'Uploading your film', 0, 90)
      const sheet = await put('stills.png', stills.sheet, 'image/png', 'Uploading its stills', 90, 6)
      const poster = await put('poster.jpg', stills.poster, 'image/jpeg', 'Uploading its poster', 96, 3)
      setStep('Entering it…'); setProgress(99)
      const r = await authFetch('/api/festival', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title.trim(), line: line.trim(), seconds: stills.seconds, video: video.url, sheet: sheet.url, poster: poster.url, agree: true }),
      })
      const b = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(b.error || 'It did not go in.')
      onDone(`"${title.trim()}" is entered. It waits for a look, and Pino hears about it once it's in.`)
    } catch (err) {
      setError(err.message || 'It did not go in.')
    } finally { setStep(null); setProgress(null) }
  }

  const sending = progress != null
  return (
    <div className="grid gap-6 md:grid-cols-5 items-start" style={{ paddingTop: 6 }}>
      <div className="md:col-span-3 min-w-0">
        <Step n={1} title="Your film" done={!!stills}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => !sending && input.current?.click()}
            onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !sending) input.current?.click() }}
            onDragOver={(e) => { e.preventDefault(); setOver(true) }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => { e.preventDefault(); setOver(false); if (!sending) pick(e.dataTransfer.files?.[0] || null) }}
            style={{
              border: `2.5px dashed ${over ? MAGENTA : 'rgba(47,38,80,0.4)'}`, borderRadius: 16, background: over ? '#fbe3e9' : PAPER,
              padding: preview ? 10 : '26px 18px', textAlign: 'center', cursor: sending ? 'default' : 'pointer', transition: 'background 150ms ease',
            }}
          >
            {preview ? (
              <div>
                {/* the stills in a strip of film, sprocket holes and all: how Pino will watch it */}
                <div style={{ background: '#15121c', borderRadius: 10, padding: '14px 10px', position: 'relative' }}>
                  <Sprockets top />
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={preview} alt="Its stills, in the order they play" style={{ width: '100%', display: 'block', borderRadius: 4 }} />
                  <Sprockets />
                </div>
                <div className="flex items-center justify-between flex-wrap" style={{ gap: 8, marginTop: 10 }}>
                  <span style={{ ...SOFT, color: kawaii.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>{file.name}</span>
                  <span className="flex" style={{ gap: 6 }}><Pill bg={kawaii.ink}>{Math.round(stills.seconds)} seconds</Pill><Pill bg={kawaii.cream}>change</Pill></span>
                </div>
              </div>
            ) : (
              <>
                <div style={{ fontFamily: DISPLAY, fontSize: 22, textTransform: 'uppercase', color: kawaii.ink }}>{step ? 'Reading your film…' : 'Drop your film here'}</div>
                <div style={{ ...SOFT, marginTop: 4 }}>or click to choose one</div>
                <div className="flex justify-center flex-wrap" style={{ gap: 6, marginTop: 12 }}>
                  {['silent', `${SECONDS[0]}–${SECONDS[1]} seconds`, 'mp4 · mov · webm', 'up to 50 MB'].map((t) => <Pill key={t} bg={kawaii.cream}>{t}</Pill>)}
                </div>
              </>
            )}
            <input ref={input} type="file" accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm" hidden onChange={(e) => pick(e.target.files?.[0] || null)} />
          </div>
          {preview && <p style={{ ...SOFT, marginTop: 8 }}>These twelve stills, in this order, are how Pino watches it.</p>}
        </Step>

        <Step n={2} title="What it's called" done={!!title.trim()}>
          <Field label="Its title" count={`${title.length}/${TITLE_MAX}`}>
            <input type="text" value={title} maxLength={TITLE_MAX} onChange={(e) => setTitle(e.target.value)} placeholder="The title, as it's shown" style={field} />
          </Field>
          <div style={{ height: 10 }} />
          <Field label="One line about it, in your own words (Pino reads this)" count={`${line.length}/${LINE_MAX}`}>
            <textarea value={line} maxLength={LINE_MAX} rows={2} onChange={(e) => setLine(e.target.value)} placeholder="Optional" style={{ ...field, resize: 'vertical' }} />
          </Field>
        </Step>

        <Step n={3} title="Your word" done={made && agree}>
          <div className="flex flex-col" style={{ gap: 8 }}>
            <Tick on={made} set={setMade}>I made this film, and I have the right to use everything in it.</Tick>
            <Tick on={agree} set={setAgree}>I agree to the <button type="button" onClick={toRules} style={{ textDecoration: 'underline', fontWeight: 800, color: kawaii.ink }}>rules and the small print</button>.</Tick>
          </div>
        </Step>

        <div style={{ paddingLeft: 48 }}>
          {sending && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ height: 14, borderRadius: 999, background: kawaii.creamDeep, border: kawaii.outlineThin, overflow: 'hidden' }}>
                <div style={{ width: `${Math.round(progress)}%`, height: '100%', background: MAGENTA, transition: 'width 200ms ease' }} />
              </div>
              <div style={{ ...SOFT, marginTop: 6 }}>{step} · {Math.round(progress)}%</div>
            </div>
          )}
          {error && <p style={{ ...SOFT, color: '#b0413e', marginBottom: 10 }}>{error}</p>}
          <button type="button" style={bigButton(!!ready && !sending)} disabled={!ready || sending} onClick={send}>
            {sending ? 'Sending…' : 'Enter it into the festival'}
          </button>
        </div>
      </div>

      <div className="md:col-span-2 min-w-0 flex flex-col" style={{ gap: 12 }}>
        <Card style={{ background: PAPER }}>
          <div style={KICKER}>Before you send it</div>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18, listStyle: 'disc' }}>
            {['One film each. You can take it back out before the night.', 'A person watches it before it goes in.', 'Pino hears about it once it\'s in, and watches it on festival night.', 'Your username goes with it. Your email never does.'].map((t) => (
              <li key={t} style={{ ...P, fontSize: 13.5, lineHeight: 1.55, marginBottom: 4 }}>{t}</li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  )
}

function Sprockets({ top = false }) {
  return (
    <div aria-hidden style={{ position: 'absolute', left: 8, right: 8, [top ? 'top' : 'bottom']: 3, display: 'flex', justifyContent: 'space-between' }}>
      {Array.from({ length: 16 }, (_, i) => <span key={i} style={{ width: 8, height: 6, borderRadius: 2, background: '#f4e6cc55' }} />)}
    </div>
  )
}

const field = { width: '100%', border: kawaii.outlineThin, borderRadius: 12, padding: '10px 12px', background: '#fffaf2', color: kawaii.ink, fontWeight: 600, fontSize: 14.5, boxShadow: 'inset 0 2px 0 rgba(47,38,80,0.06)' }

function Field({ label, count, children }) {
  return (
    <label style={{ display: 'block' }}>
      <span className="flex justify-between" style={{ ...SOFT, marginBottom: 5 }}><span>{label}</span><span>{count}</span></span>
      {children}
    </label>
  )
}

function Tick({ on, set, children }) {
  return (
    <label
      className="flex items-start"
      style={{ gap: 10, padding: '10px 12px', border: kawaii.outlineThin, borderRadius: 12, background: on ? '#e6f5e9' : '#fffaf2', cursor: 'pointer', boxShadow: on ? 'none' : kawaii.dropSm }}
    >
      <input type="checkbox" checked={on} onChange={(e) => set(e.target.checked)} style={{ marginTop: 3, width: 16, height: 16, accentColor: MAGENTA }} />
      <span style={{ ...P, fontSize: 13.5, lineHeight: 1.5 }}>{children}</span>
    </label>
  )
}
